<div class="w-full space-y-6">
  <!-- Account Overview Card -->
  <p-card header="Account Overview" class="shadow-lg">
    <div class="flex items-start justify-between mb-6">
      <div class="flex items-center space-x-4">
        <!-- Account Avatar -->
        <div class="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center">
          <i class="pi pi-building text-2xl text-sky-600"></i>
        </div>
        
        <!-- Account Info -->
        <div>
          <h2 class="text-2xl font-semibold text-gray-800">{{ accountInfo.accountName }}</h2>
          <div class="flex items-center space-x-2 mt-1">
            <span class="text-sm text-gray-600">Status:</span>
            <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusBadgeClass(accountInfo.accountStatus)">
              {{ accountInfo.accountStatus }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Edit Button -->
      <p-button class="bg-sky-500 text-white px-4 py-2 rounded-md"
        [label]="isEditing ? 'Cancel' : 'Edit'" 
        [icon]="isEditing ? 'pi pi-times' : 'pi pi-pencil'"
        (onClick)="toggleEdit()">
      </p-button>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-sky-50 p-4 rounded-lg">
        <div class="text-sm text-sky-600 font-medium">Account Owner</div>
        <div class="text-lg font-semibold text-gray-800">{{ accountInfo.accountOwner }}</div>
      </div>
      <div class="bg-sky-50 p-4 rounded-lg">
        <div class="text-sm text-sky-600 font-medium">Contact Email</div>
        <div class="text-lg font-semibold text-gray-800">{{ accountInfo.contactEmail }}</div>
      </div>
      <div class="bg-sky-50 p-4 rounded-lg">
        <div class="text-sm text-sky-600 font-medium">WhatsApp</div>
        <div class="text-lg font-semibold text-gray-800">{{ accountInfo.contactWhatsApp }}</div>
      </div>
    </div>
  </p-card>

  <!-- Account Settings Card -->
  <p-card header="Account Settings" class="shadow-lg">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Account Name -->
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
        <input 
          pInputText 
          [(ngModel)]="accountInfo.accountName" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>

      <!-- Account Status -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Account Status</label>
        <select 
          [(ngModel)]="accountInfo.accountStatus"
          [disabled]="!isEditing"
          class="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          [class.bg-gray-50]="!isEditing">
          <option *ngFor="let option of accountStatusOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- Account Owner -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Account Owner</label>
        <input 
          pInputText 
          [(ngModel)]="accountInfo.accountOwner" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>

      <!-- Legal Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Legal Name</label>
        <input 
          pInputText 
          [(ngModel)]="accountInfo.accountSettings.legalName" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>

      <!-- Business Type -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
        <select 
          [(ngModel)]="accountInfo.accountSettings.businessType"
          [disabled]="!isEditing"
          class="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          [class.bg-gray-50]="!isEditing">
          <option *ngFor="let option of businessTypeOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- GST Number -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
        <input 
          pInputText 
          [(ngModel)]="accountInfo.accountSettings.gstNumber" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>

      <!-- Business Category -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Business Category</label>
        <select 
          [(ngModel)]="accountInfo.accountSettings.businessCategory"
          [disabled]="!isEditing"
          class="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          [class.bg-gray-50]="!isEditing">
          <option *ngFor="let option of businessCategoryOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- Currency -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Currency</label>
        <select 
          [(ngModel)]="accountInfo.accountSettings.currency"
          [disabled]="!isEditing"
          class="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          [class.bg-gray-50]="!isEditing">
          <option *ngFor="let option of currencyOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- Billing Address -->
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
        <textarea 
          [(ngModel)]="accountInfo.accountSettings.billingAddress"
          [readonly]="!isEditing"
          rows="3"
          class="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"
          [class.bg-gray-50]="!isEditing">
        </textarea>
      </div>

      <!-- Contact Email -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
        <input 
          type="email"
          pInputText 
          [(ngModel)]="accountInfo.contactEmail" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>

      <!-- Contact WhatsApp -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Contact WhatsApp</label>
        <input 
          type="tel"
          pInputText 
          [(ngModel)]="accountInfo.contactWhatsApp" 
          [readonly]="!isEditing"
          class="w-full"
          [class.bg-gray-50]="!isEditing">
      </div>
    </div>

    <!-- Save Button -->
    <div *ngIf="isEditing" class="flex justify-end mt-6">
      <p-button 
        label="Save Changes" 
        icon="pi pi-check"
        severity="success"
        (onClick)="saveAccountInfo()">
      </p-button>
    </div>
  </p-card>

  <!-- Account Actions Card -->
  <p-card header="Account Actions" class="shadow-lg">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <div class="flex items-center space-x-3">
          <i class="pi pi-exclamation-triangle text-yellow-600 text-xl"></i>
          <div>
            <h3 class="font-semibold text-yellow-800">Account Suspension</h3>
            <p class="text-sm text-yellow-700">Suspend this account temporarily</p>
          </div>
        </div>
        <button class="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-md text-sm hover:bg-yellow-700 transition-colors">
          Suspend Account
        </button>
      </div>

      <div class="bg-red-50 p-4 rounded-lg border border-red-200">
        <div class="flex items-center space-x-3">
          <i class="pi pi-trash text-red-600 text-xl"></i>
          <div>
            <h3 class="font-semibold text-red-800">Delete Account</h3>
            <p class="text-sm text-red-700">Permanently delete this account</p>
          </div>
        </div>
        <button class="mt-3 bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 transition-colors">
          Delete Account
        </button>
      </div>
    </div>
  </p-card>
</div>